import { useAuth0 } from '@auth0/auth0-react';
import { resolveWidgetEmbed } from '@syncanix/shared';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { env } from '@/config/env';
import { useWorkspace } from '@/core/providers/WorkspaceProvider';

import { buildWidgetContext } from './build-widget-context';
import { injectWidgetScript } from './inject-widget-script';

/**
 * Runtime surface the widget loader installs on `window` (subset we use). Typed
 * locally because `apps/web` can't import `apps/widget`'s declarations — see the
 * runtime-API table in `docs/runbooks/widget-embed.md`.
 */
interface SyncanixWindowApi {
  setTokenProvider(provider: (() => Promise<string | undefined>) | undefined): void;
  /**
   * SYN-10 / ADR-0150 — per-call headers for the CUSTOMER-API leg. Distinct from
   * `setTokenProvider`, which authenticates the TURN to Syncanix; this
   * authenticates the capability CALL to the customer's API (here, our own).
   * Intent-blind: the provider sees `{ toolName, args }` and never the signed
   * intent, and reserved `x-syncanix-*` headers can never be overwritten.
   */
  setHeadersProvider(
    provider: ((call: { toolName: string }) => Promise<Record<string, string>>) | undefined,
  ): void;
  // `object` (not `Record<string, unknown>`) so a typed context literal is
  // assignable without an index signature; the runtime accepts any plain object.
  setContext(context: object): void;
  clearContext(): void;
}
declare global {
  interface Window {
    syncanix?: SyncanixWindowApi;
  }
}

/**
 * Dogfood: mount Syncanix's OWN chat widget inside the authenticated dashboard,
 * acting as the signed-in user.
 *
 * Renders nothing (the widget owns its Shadow-DOM host). A client-only effect
 * injects the loader when `VITE_SYNCANIX_WIDGET_KEY` is configured, then — once
 * the loader installs `window.syncanix` — registers:
 *   - `setTokenProvider`: returns the signed-in user's Auth0 access token, so
 *     the agent acts AS that user (their JWT, their scopes, their audit trail).
 *     Fail-soft: a thrown/empty return just means the server sees no verified
 *     identity for that turn.
 *   - `setContext`: the current route + workspace, re-fed on navigation, so the
 *     assistant is grounded in where the user is. NEVER carries credentials.
 *
 * No key configured → a pure no-op (the common customer-dashboard build).
 * Mounted ONCE at the AppShell level so SPA navigation never tears it down.
 */
export function DashboardWidget(): null {
  const { getAccessTokenSilently } = useAuth0();
  const { pathname } = useLocation();
  const { currentWorkspace } = useWorkspace();
  const workspaceName = currentWorkspace?.name ?? null;

  // Whether THIS app embedded the widget (a key is configured). Gates the
  // context effect so we never feed context to a widget we didn't mount. `env`
  // is a module constant, so this is stable across renders.
  const embedConfig = resolveWidgetEmbed({
    key: env.VITE_SYNCANIX_WIDGET_KEY,
    origin: env.VITE_SYNCANIX_WIDGET_ORIGIN,
    apiBaseUrl: env.VITE_SYNCANIX_WIDGET_API_BASE_URL,
  });
  const embedded = embedConfig !== null;

  // Refs hold the LATEST token getter + context so the once-registered provider
  // and the load handler always read current values without re-injecting.
  const tokenGetterRef = useRef(getAccessTokenSilently);
  tokenGetterRef.current = getAccessTokenSilently;
  const contextRef = useRef(buildWidgetContext({ pathname, workspaceName }));
  contextRef.current = buildWidgetContext({ pathname, workspaceName });

  useEffect(() => {
    if (embedConfig === null) return;

    let cancelled = false;
    const wire = (): void => {
      if (cancelled || !window.syncanix) return;
      window.syncanix.setTokenProvider(async () => {
        try {
          return await tokenGetterRef.current();
        } catch {
          return undefined;
        }
      });
      window.syncanix.setContext(contextRef.current);
      // Without this the dashboard's own capability calls reach the API with no
      // Authorization header and 401. Fail-soft returns {} rather than
      // `Bearer undefined`: an absent header is an honest anonymous call, a
      // malformed one is a confusing rejection.
      window.syncanix.setHeadersProvider(async () => {
        try {
          const token = await tokenGetterRef.current();
          return token !== '' ? { Authorization: `Bearer ${token}` } : {};
        } catch {
          return {};
        }
      });
    };

    const script = injectWidgetScript(embedConfig);
    if (window.syncanix) {
      wire();
    } else if (script !== null) {
      script.addEventListener('load', wire, { once: true });
    }

    return () => {
      cancelled = true;
      window.syncanix?.setTokenProvider(undefined);
      window.syncanix?.setHeadersProvider(undefined);
      window.syncanix?.clearContext();
    };
    // Mount-once: inject + wire a single loader. The provider/context read live
    // values via refs, so this effect never needs to re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-feed context on navigation / workspace switch (no-op until ready, and
  // only when we actually embedded the widget).
  useEffect(() => {
    if (!embedded) return;
    window.syncanix?.setContext(buildWidgetContext({ pathname, workspaceName }));
  }, [pathname, workspaceName, embedded]);

  return null;
}
